<template>
	<v-layout class="messages" row :wrap="$vuetify.breakpoint.smAndDown">
		<v-flex xs12 md6 lg5 class="messages__col" pt-0>
			<v-layout row wrap>
				<v-flex xs12>
					<v-form ref="form">
						<v-layout :row="$vuetify.breakpoint.smAndDown" class="messages__header">
							<v-flex xs12 class="documents-display__toolbar">
								<v-toolbar flat dark height="35" color="secondary">
									<v-toolbar-title>New Message</v-toolbar-title>
								</v-toolbar>
							</v-flex>
							<v-flex xs12 sm6 md3 class="documents-display__sort">
								<v-autocomplete
									v-model="form.to"
									:loading="loading"
									:items="recipients"
									:search-input.sync="search"
									cache-items
									item-text="name"
									item-value="id"
									solo
									flat
									dense
									multiple
									hide-no-data
									hide-details
									placeholder="Select recipient"
									height="35"
									style="margin: 0; padding: 0"
									:class="!dark ? 'white' : ''"
									:color="dark ? 'white' : ''"
									clearable
								>
									<template v-slot:selection="{ item, index }">
										<span v-if="form.to.length === 1">{{ item.name }}</span>
										<span v-if="index === 1">{{ form.to.length }} Recipients</span>
									</template>
								</v-autocomplete>
							</v-flex>
						</v-layout>

						<v-layout row>
							<v-flex>
								<v-card>
									<v-textarea
										name="body"
										autofocus
										solo
										flat
										:rows="3"
										:background-color="!dark ? 'white' : ''"
										placeholder="Send a Message..."
										auto-grow
										v-model="form.body"
										@keydown.meta.enter="send"
									></v-textarea>

									<v-card-actions>
										<v-spacer></v-spacer>
										<v-tooltip top>
											<template v-slot:activator="{ on }">
												<input
													type="file"
													ref="attachments"
													style="display:none;"
													multiple
													:accept="availableAttachments"
													@change="changeAttachments"
												>
												<v-btn icon @click="attachFile" v-on="on">
													<div class="v-icon">
														<fa-icon :icon="['far', 'paperclip']" size="xs"/>
													</div>
												</v-btn>
											</template>
											<span>Attach File</span>
										</v-tooltip>

										<v-tooltip top>
											<template v-slot:activator="{ on }">
												<v-btn icon @click="send" v-on="on" :loading="loading" :disabled="loading">
													<div class="v-icon">
														<fa-icon :icon="['far', 'paper-plane']" size="xs"/>
													</div>
												</v-btn>
											</template>
											<span>Send</span>
										</v-tooltip>
									</v-card-actions>
									<v-card-text v-if="form.attachments.length" class="accent lighten-4 py-2">
										<div v-for="attachment in form.attachments" :key="attachment.name">
											<fa-icon :icon="['far', 'paperclip']" fixed-width size="sm"/>
											{{attachment.name}}
										</div>
									</v-card-text>
								</v-card>
							</v-flex>
						</v-layout>
					</v-form>
				</v-flex>
			</v-layout>

			<v-layout row wrap>
				<message-list type="unread" :badge="true"></message-list>
			</v-layout>
		</v-flex>

		<v-flex lg1 d-flex justify-center v-if="$vuetify.breakpoint.mdAndUp">
			<v-divider vertical></v-divider>
		</v-flex>

		<v-flex xs12 md6 lg5 class="messages__col">
			<v-layout row wrap v-if="!viewSent">
				<message-list type="read">
					<template v-slot:sentCheckbox>
						<div class="documents-display__checkbox">
							<v-checkbox v-model="viewSent" hide-details color="white" :ripple="false" label="View Sent"></v-checkbox>
						</div>
					</template>
				</message-list>
			</v-layout>

			<v-layout row wrap v-else>
				<message-list type="sent">
					<template v-slot:sentCheckbox>
						<div class="documents-display__checkbox">
							<v-checkbox v-model="viewSent" hide-details color="white" :ripple="false" label="View Sent"></v-checkbox>
						</div>
					</template>
				</message-list>
			</v-layout>
		</v-flex>
	</v-layout>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import MessageList from "@views/messages/MessageList";
import Errors from "@root/helpers/Errors";

export default {
	data() {
		return {
			form: {
				to: "",
				body: "",
				attachments: []
			},
			loading: false,
			search: "",
			viewSent: false,
			errors: new Errors()
		};
	},

	components: { MessageList },

	computed: {
		...mapGetters(["dark", "user", "currentTeam"]),

		...mapGetters("messages", {
			RECIPIENTS: "recipients"
		}),

		recipients() {
			if (this.RECIPIENTS) {
				return this.RECIPIENTS.filter(recipient => {
					if (!this.search) {
						return true;
					}

					let term1 = recipient.name.toLowerCase(),
						term2 = recipient.email.toLowerCase(),
						searchTerm = this.search.toLowerCase();

					return (
						term1.indexOf(searchTerm) !== -1 ||
						term2.indexOf(searchTerm) !== -1
					);
				});
			}

			return [];
		},

		availableAttachments() {
			let accept = this.currentTeam.acceptable_extensions;

			accept = accept.split(",");
			accept = accept.map(item => "." + item);

			return accept.join(", ");
		}
	},

	methods: {
		...mapActions("messages", ["getMessages", "getSentMessages"]),

		...mapActions("messages", {
			GET_RECIPIENTS: "getRecipients",
			POST_MESSAGE: "postMessage"
		}),

		attachFile() {
			this.$refs.attachments.click();
		},

		send() {
			this.loading = true;

			let formData = new FormData();

			_.forEach(this.form, (value, key) => {
				if (key !== "attachments") {
					formData.append(key, value);
				}
			});

			_.forEach(this.$refs.attachments.files, file => {
				formData.append("attachments[]", file);
			});

			this.POST_MESSAGE(formData)
				.then(data => {
					this.loading = false;

					this.$refs.form.reset();
					this.form = {
						to: "",
						body: "",
						attachments: []
					};
				})
				.catch(error => {
					this.loading = false;

					error = Object.assign({}, error);
					this.errors.record(error.response.data.errors);

					let errorText = "Please ";

					if (this.errors.has("to")) {
						errorText += "select a recipient";
					}

					if (this.errors.has("body")) {
						if (this.errors.has("to")) {
							errorText += " and ";
						}

						errorText += "enter a message";
					}

					this.$swal({
						text: errorText + ".",
						type: "error",
						confirmButtonText: "Okay"
					});
				});
		},

		reset() {
			this.$refs.form.reset();
		},

		getRecipients(val) {
			if (this.recipients.length === 0) {
				this.GET_RECIPIENTS();
			}
		},

		changeAttachments(value) {
			this.form.attachments = value.target.files;
		}
	},

	created() {
		this.getMessages({ type: ["unread", "read"] });
		this.getSentMessages({ type: ["sent"] });
	},

	watch: {
		search(val) {
			this.getRecipients(val);
		}
	}
};
</script>
